/**
 * Search Dropdown Component
 */
import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search } from 'lucide-react';
import type { SearchResult } from '../types';

interface SearchDropdownProps {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  showDropdown: boolean;
  setShowDropdown: (show: boolean) => void;
  selectedIndex: number;
  setSelectedIndex: (index: number) => void;
  results: SearchResult[];
  onKeyDown: (e: React.KeyboardEvent) => void;
  onResultClick: (item: SearchResult) => void;
  inputRef: React.RefObject<HTMLInputElement>;
  dropdownRef: React.RefObject<HTMLDivElement>;
  t: (en: string, no: string) => string;
}

export const SearchDropdown: React.FC<SearchDropdownProps> = ({
  searchQuery,
  setSearchQuery,
  showDropdown,
  setShowDropdown,
  selectedIndex,
  setSelectedIndex,
  results,
  onKeyDown,
  onResultClick,
  inputRef,
  dropdownRef,
  t,
}) => (
  <div className="absolute top-4 right-4 sm:top-6 sm:right-6 z-50">
    <div className="relative">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
      <input
        ref={inputRef}
        type="text"
        value={searchQuery}
        onChange={(e) => { setSearchQuery(e.target.value); setShowDropdown(true); setSelectedIndex(0); }}
        onFocus={() => setShowDropdown(true)}
        onKeyDown={onKeyDown}
        placeholder={t('Search cases...', 'Søk i saker...')}
        className="w-48 sm:w-56 pl-9 pr-4 py-2.5 bg-white border border-gray-200 hover:border-gray-300 focus:border-gray-400 rounded-full text-sm text-gray-900 placeholder-gray-400 focus:outline-none transition-all shadow-sm"
      />
      
      <AnimatePresence>
        {showDropdown && results.length > 0 && (
          <motion.div
            ref={dropdownRef}
            initial={{ opacity: 0, y: -8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.96 }}
            transition={{ duration: 0.15 }}
            className="absolute top-full right-0 w-72 mt-2 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden"
          >
            <div className="py-2">
              {results.map((item: SearchResult, idx: number) => (
                <div
                  key={item.id}
                  onClick={() => onResultClick(item)}
                  className={`px-4 py-2.5 cursor-pointer transition-colors ${
                    idx === selectedIndex ? 'bg-gray-100' : 'hover:bg-gray-50'
                  } ${item.type === 'empty' ? 'cursor-default text-gray-400' : 'text-gray-900'}`}
                >
                  <div className={`text-sm font-medium truncate ${item.type === 'action' ? 'text-black' : ''}`}>
                    {item.title}
                  </div>
                  {item.subtitle && (
                    <div className="text-xs text-gray-400 truncate mt-0.5">{item.subtitle}</div>
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  </div>
);

export default SearchDropdown;
